import React, { useRef, useState } from 'react';
import {
  faBars,
  faSignOut,
  Icon,
  Icons,
  IconDefinition,
  faCircleUser,
} from '../Icon';
import { AppRoutes } from '@/routes';
import { useTheme } from 'styled-components';
import { useTranslate } from '@/translations';
import { loginRequest } from '@/auth/config';
import { Button, Drawer, Tooltip } from 'antd';
import { useLogout } from '@/api/Users';
import {
  HeaderTools,
  MenuButton,
  MobileActionButton,
  MobileActionLink,
  MobileActions,
  MobileMenuSection,
  NavActions,
  NavLinkDesktop,
  NavLinkContent,
  NavLogo,
  TenantSwitcherSlot,
} from './Navigation.styled';
import { useCompactViewport, useIdTokenClaims } from '@/hooks';
import { Logo } from '../Logo';
import { Slider } from '../Slider';
import { TenantSwitcher } from '../TenantSwitcher';
import { useComms } from '@/modules/comms/context/CommsContext';
import { useCall } from '@/modules/comms/call/CallContext';
import { GlobalInbox } from '@/modules/comms/components/GlobalInbox';

type NavLinkProps = {
  to: string;
  icon: Icons | IconDefinition;
  color: string;
  label: string;
};

const NavLink = ({ to, icon, color, label }: NavLinkProps) => (
  <Tooltip title={label}>
    <NavLinkContent to={to}>
      <div>
        <NavLinkDesktop color={color}>
          <Icon icon={icon} />
        </NavLinkDesktop>
      </div>
    </NavLinkContent>
  </Tooltip>
);

export const Navigation = () => {
  const { primaryBg } = useTheme();
  const { t } = useTranslate();
  const { instance, hasLoggedIn, idTokenClaims } = useIdTokenClaims();
  const userEmail = idTokenClaims?.signInName || '';

  const { mutateAsync: logoutAsync } = useLogout();
  const { stop } = useComms();
  const { hangUp } = useCall();
  const compact = useCompactViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const inboxOpenRef = useRef(false);

  const handleLogout = async () => {
    await new Promise<void>((resolve) => {
      const timeout = window.setTimeout(resolve, 3000);
      const finish = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      void Promise.resolve()
        .then(hangUp)
        .catch(() => undefined)
        .then(() => stop({ endSession: true }))
        .then(finish, finish);
    });
    await logoutAsync();
    instance.logout();
  };

  const handleLogin = () => {
    instance
      .loginRedirect({
        scopes: loginRequest.scopes,
        prompt: 'login',
      })
      .catch((e: Error) => {
        console.error('login error', e);
      });
  };

  return (
    <>
      <NavLogo to={AppRoutes.root}>
        <Logo />
      </NavLogo>
      {hasLoggedIn && compact ? (
        <>
          <MenuButton
            type="text"
            aria-label={t('menu')}
            aria-expanded={menuOpen}
            data-testid="app-menu-button"
            onClick={() => setMenuOpen(true)}
            icon={<Icon icon={faBars} />}
          />
          <Drawer
            title={t('menu')}
            placement="right"
            width={300}
            open={menuOpen}
            forceRender
            push={false}
            onClose={(event) => {
              const target = event.target;
              const closeButton =
                target instanceof Element &&
                !!target.closest('.ant-drawer-close');
              if (inboxOpenRef.current && !closeButton) return;
              inboxOpenRef.current = false;
              setMenuOpen(false);
            }}
            styles={{
              body: {
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                overflow: 'hidden',
                minHeight: 0,
              },
            }}
          >
            <MobileMenuSection>
              <TenantSwitcher />
            </MobileMenuSection>
            <Slider variant="panel" onNavigate={() => setMenuOpen(false)} />
            <MobileActions>
              <GlobalInbox
                variant="menu"
                applicationIdentifier={process.env.VITE_APP_NOVU_APP_ID ?? ''}
                subscriber={userEmail}
                callerSubject={idTokenClaims?.sub ?? ''}
                color={primaryBg}
                onOpenChange={(next) => {
                  inboxOpenRef.current = next;
                }}
              />
              <MobileActionLink
                to={AppRoutes.manageUser}
                onClick={() => setMenuOpen(false)}
              >
                <Icon icon={faCircleUser} />
                {t('myAccount')}
              </MobileActionLink>
              <MobileActionButton
                type="button"
                data-testid="app-log-out"
                onClick={handleLogout}
              >
                <Icon icon={faSignOut} />
                {t('logOut')}
              </MobileActionButton>
            </MobileActions>
          </Drawer>
        </>
      ) : (
        <HeaderTools>
          {hasLoggedIn ? (
            <>
              <TenantSwitcherSlot>
                <TenantSwitcher />
              </TenantSwitcherSlot>
              <NavActions>
                <GlobalInbox
                  applicationIdentifier={process.env.VITE_APP_NOVU_APP_ID ?? ''}
                  subscriber={userEmail}
                  callerSubject={idTokenClaims?.sub ?? ''}
                  color={primaryBg}
                />

                <NavLink
                  to={AppRoutes.manageUser}
                  icon={faCircleUser}
                  color={primaryBg}
                  label={t('myAccount')}
                />

                <Tooltip title={t('logOut')}>
                  <Button
                    type="text"
                    onClick={handleLogout}
                    style={{ padding: 0 }}
                  >
                    <NavLinkDesktop color={primaryBg}>
                      <Icon icon={faSignOut} />
                    </NavLinkDesktop>
                  </Button>
                </Tooltip>
              </NavActions>
            </>
          ) : (
            <NavActions>
              <Button type="primary" size="large" onClick={handleLogin}>
                {t('logIn')}
              </Button>
            </NavActions>
          )}
        </HeaderTools>
      )}
    </>
  );
};
